/**
 * Normalizacion de las respuestas del Asistente del Asesor (hermes).
 *
 * Dos fuentes con convenciones distintas, a proposito separadas aca para que
 * la UI no tenga que acordarse de cual es cual:
 *
 *   - `/advisor-metrics` (spec 006): razones en 0-1, `null` cuando no hay
 *     denominador.
 *   - `/lead-surveys/me/*` (spec 010): razones ya en PORCENTAJE (0-100, un
 *     decimal) y siempre numericas — el backend devuelve 0 tanto cuando el
 *     resultado es 0% como cuando no hubo denominador. Distinguirlos es
 *     trabajo del front: ver `surveyRate`.
 *
 * El despliegue es en dos repos (hermes primero, este front despues), asi que
 * cualquier campo puede faltar: los conteos degradan a 0.
 */

const count = value => (Number.isFinite(value) ? value : 0)

const rate = value => (Number.isFinite(value) ? value : null)

// ─── Citas (spec 006) ──────────────────────────────────────────────────

export const EMPTY_ADVISOR_METRICS = {
  appointmentsScheduled: 0,
  appointmentsCompleted: 0,
  appointmentsRescheduled: 0,
  cancelledByAdvisor: 0,
  cancelledByLead: 0,
  noShowsLead: 0,
  noShowsAdvisor: 0,
  conversionRate: null,
  avgResponseTimeMs: 0,
  leadsAttendedByAdvisor: 0,
  leadsAssignedTotal: 0,
  conversationsNotified: 0,
  conversationsAnswered: 0,
  responseRate: null,
  byDay: [],
}

export const advisorMetricsSelector = (data = {}) => ({
  appointmentsScheduled: count(data.appointmentsScheduled),
  appointmentsCompleted: count(data.appointmentsCompleted),
  appointmentsRescheduled: count(data.appointmentsRescheduled),
  cancelledByAdvisor: count(data.cancelledByAdvisor),
  cancelledByLead: count(data.cancelledByLead),
  noShowsLead: count(data.noShowsLead),
  noShowsAdvisor: count(data.noShowsAdvisor),
  conversionRate: rate(data.conversionRate),
  avgResponseTimeMs: count(data.avgResponseTimeMs),
  leadsAttendedByAdvisor: count(data.leadsAttendedByAdvisor),
  leadsAssignedTotal: count(data.leadsAssignedTotal),
  conversationsNotified: count(data.conversationsNotified),
  conversationsAnswered: count(data.conversationsAnswered),
  responseRate: rate(data.responseRate),
  byDay: Array.isArray(data.byDay)
    ? data.byDay
        .filter(point => point && point.date)
        .map(point => ({ date: point.date, count: count(point.count) }))
    : [],
})

/** Total de cancelaciones, sin importar quien las inicio. */
export const totalCancellations = metrics =>
  count(metrics && metrics.cancelledByAdvisor) +
  count(metrics && metrics.cancelledByLead)

/**
 * `true` cuando el periodo no tiene ninguna actividad del asistente. Se usa
 * para mostrar un vacio explicito en vez de un tablero lleno de ceros.
 */
export const hasNoActivity = metrics =>
  Boolean(metrics) &&
  count(metrics.appointmentsScheduled) === 0 &&
  count(metrics.appointmentsRescheduled) === 0 &&
  totalCancellations(metrics) === 0 &&
  count(metrics.noShowsLead) === 0 &&
  count(metrics.leadsAssignedTotal) === 0 &&
  count(metrics.conversationsNotified) === 0

// ─── Encuesta de satisfaccion del lead (spec 010) ──────────────────────

/**
 * Una razon de la encuesta solo significa algo si su denominador existe.
 * Hermes manda 0 en los dos casos; aca se convierte en `null` cuando no hubo
 * denominador para que la UI pinte un guion. Un "0% de leads no atendidos"
 * suena a operacion impecable cuando en realidad nadie contesto la encuesta.
 */
const surveyRate = (value, denominator) =>
  count(denominator) > 0 ? count(value) : null

const distribution = items =>
  Array.isArray(items)
    ? items
        .filter(item => item && item.label !== undefined)
        .map(item => ({
          label: String(item.label),
          count: count(item.count),
          percentage: count(item.percentage),
        }))
    : []

export const EMPTY_SURVEY_METRICS = {
  sent: 0,
  responded: 0,
  completed: 0,
  responseRate: null,
  completionRate: null,
  optOutRate: null,
  notAttendedCount: 0,
  notAttendedRate: null,
  responseTimeDistribution: [],
  avgRating: null,
  ratingDistribution: [],
  doubtsResolvedDistribution: [],
  npsSimplified: null,
}

export const advisorSurveyMetricsSelector = (data = {}) => {
  const sent = count(data.sent)
  const responded = count(data.responded)

  return {
    sent,
    responded,
    completed: count(data.completed),
    responseRate: surveyRate(data.responseRate, sent),
    completionRate: surveyRate(data.completionRate, responded),
    optOutRate: surveyRate(data.optOutRate, sent),

    notAttendedCount: count(data.notAttendedCount),
    // Sobre los que RESPONDIERON: del que no contesto no sabemos nada.
    notAttendedRate: surveyRate(data.notAttendedRate, responded),
    responseTimeDistribution: distribution(data.responseTimeDistribution),

    avgRating: rate(data.avgRating),
    ratingDistribution: distribution(data.ratingDistribution),
    doubtsResolvedDistribution: distribution(data.doubtsResolvedDistribution),
    // El backend no expone cuantos contestaron la P5; `responded` es la mejor
    // cota disponible para saber si el NPS tiene sustento.
    npsSimplified: surveyRate(data.npsSimplified, responded),
  }
}

export const advisorSurveyCommentsSelector = (data = {}) => ({
  items: Array.isArray(data.items)
    ? data.items
        .filter(item => item && item.comment)
        .map(item => ({
          comment: item.comment,
          rating: rate(item.rating),
          monthLabel: item.monthLabel || '',
        }))
    : [],
  total: count(data.total),
  page: count(data.page) || 1,
  limit: count(data.limit) || 0,
})

/** `true` cuando no se envio ninguna encuesta en el periodo. */
export const hasNoSurveys = metrics =>
  Boolean(metrics) && count(metrics.sent) === 0
